import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { QUERY_KEY_CONST } from '@/constant';
import { STATUS_COLOR_MAP } from '@/constant/taskConstant';
import { useExportTaskLabelQuery } from '@/hook';
import {
  Dataset,
  FileDownload,
  Info,
  RestartAlt,
  VButton,
  VFlex,
  VStack,
  VTooltip,
  formatDate,
} from '@/package';
import { VStatusBadge } from '@/package';
import { VIconButton } from '@/package/kiska/src/component/UI/VIconButton';
import { useCollectionManagementStore } from '@/store';
import type { S3ListItem, Task } from '@/type';

type BoxDataOperationProps = {
  task: Task;
};

const BoxDataOperation = ({ task }: BoxDataOperationProps) => {
  const { mutate: exportTaskLabel, isPending } = useExportTaskLabelQuery();
  const { downloadDirectoryList } = useCollectionManagementStore();
  const queryClient = useQueryClient();
  const [boxDownloadErrorMessage, setBoxDownloadErrorMessage] = useState('');

  const onDownloadClick = () => {
    if (!task.exportBoxPath) return;
    downloadDirectoryList([{ key: task.exportBoxPath } as S3ListItem])
      .then(() => {
        setBoxDownloadErrorMessage('');
      })
      .catch(() => {
        setBoxDownloadErrorMessage(
          'Box data folder is missing, so the download failed.\n Click ‘Regenerate’ on the right and try again.',
        );
      })
      .finally(() => {
        queryClient.invalidateQueries({
          queryKey: QUERY_KEY_CONST.TASK_KEYS.getDetail(),
        });
      });
  };

  const state = (() => {
    const opsState = task?.operation?.find(
      op => op.operationType === 'ExportLabel',
    )?.operationState;

    if (opsState === 'Failed') return 'Failed';
    if (task.state !== 'Completed' || !task.exportBoxPath) return 'Not started';

    return 'Available';
  })();

  const diabled = (() => {
    const isSomethingRunning = task?.operation?.some(
      op => op.operationState === 'Running',
    );
    return (
      isSomethingRunning ||
      task?.state !== 'Completed' ||
      state !== 'Available' ||
      isPending
    );
  })();

  const Content = () => {
    if (state === 'Not started')
      return (
        <VFlex
          justify="center"
          items="center"
          className="h-full text-body-xl font-semibold"
          style={{ color: STATUS_COLOR_MAP[state] }}
        >
          Not created yet
        </VFlex>
      );

    if (state === 'Available')
      return (
        <>
          <VFlex items="center" gap={0.5}>
            <VIconButton
              Icon={RestartAlt}
              disabled={diabled}
              outlined
              variant="secondary"
              onClick={() => exportTaskLabel(task.id)}
            />
            <VButton
              disabled={diabled}
              outlined
              size="sm"
              variant="secondary"
              onClick={() => onDownloadClick()}
            >
              <FileDownload className="h-4 w-4" />
              <div>Download</div>
            </VButton>
          </VFlex>
          {boxDownloadErrorMessage.length ? (
            <div className="flex-1 text-body-sm text-error-500">
              {boxDownloadErrorMessage}
            </div>
          ) : (
            <div className="flex-1 text-body-sm text-gray-500">
              Automatically uploaded to the task path in 'Collection'.
            </div>
          )}
        </>
      );

    if (state === 'Failed')
      return (
        <>
          <VFlex items="center" gap={0.5}>
            <VIconButton
              Icon={RestartAlt}
              disabled={diabled}
              outlined
              variant="secondary"
              onClick={() => exportTaskLabel(task.id)}
            />
            <VButton
              disabled={diabled}
              outlined
              size="sm"
              variant="secondary"
              onClick={() => onDownloadClick()}
            >
              <FileDownload className="h-4 w-4" />
              <div>Download</div>
            </VButton>
          </VFlex>
          <div className="flex-1 text-body-sm text-error-500">
            Box data creation failed. Please contact <br />{' '}
            <span className="text-gray-500">[support@vueron.com]</span>
            for assistance.
          </div>
        </>
      );
  };

  return (
    <VStack
      className="flex-1 rounded-md border border-gray-300 px-4 py-2"
      gap={0.5}
    >
      <VFlex justify="between" items="center">
        <VFlex gap={0.25} className="items-center">
          <Dataset className="h-5 w-5 text-primary-500" />
          <div className="text-body-lg font-semibold text-gray-600">
            Box data
          </div>
        </VFlex>

        <VFlex gap={0.25} items="center">
          {state === 'Available' && (
            <VTooltip
              content={`Completed: ${formatDate(task.updatedAt)}`}
              placement="bottom"
            >
              <div className="size-fit rounded-full">
                <Info className="size-4 text-info-500" />
              </div>
            </VTooltip>
          )}
          <VStatusBadge
            size="sm"
            variant={
              state === 'Available'
                ? 'completed'
                : state === 'Failed'
                  ? 'failed'
                  : 'notStarted'
            }
          >
            {state}
          </VStatusBadge>
        </VFlex>
      </VFlex>

      <div className="flex h-[38px] flex-row-reverse items-center justify-between pl-6">
        {/* eslint-disable-next-line react-hooks/static-components */}
        <Content />
      </div>
    </VStack>
  );
};

export default BoxDataOperation;
